import { forwardRef, KeyboardEvent } from 'react';
import {
  PinCodeInput,
  PseudoInputEvent,
} from '@tink/core/src/components/atoms/PinCodeInput/PinCodeInput';
import { CheckIcon } from '@tink/core/src/components/atoms/Icons/CheckIcon';
import { ProviderField } from '../../../../../../models/provider';
import { Caption } from '../../../../../atoms/Caption/Caption';
import { H3 } from '../../../../../atoms/Heading/Heading';
import { SelectProviderFieldOption } from './SelectProviderFieldOption/SelectProviderFieldOption';
import { PasswordInputField } from './PasswordInputField';
import { CheckboxField } from './CheckboxField';
import {
  InputAutoCompleteType,
  InputEnterKeyHintType,
  TextField,
  TextInputFieldType,
} from './TextField';
import {
  StyledImageField,
  StyledInputCode,
  StyledInputContainer,
  StyledLabel,
} from './InputField.styles';

export type ProviderInputState = {
  value: string;
  errorMessage: string;
  isValid: boolean;
  disabled: boolean;
  providerField: ProviderField;
  showCheckIcon: boolean;
};

export type OnChangeProps = {
  providerInputName: string;
  value: string;
  onFormSubmit: boolean;
};

export type InputProps = {
  index: number;
  providerInputState: ProviderInputState;
  onChange: ({ providerInputName, value }: OnChangeProps) => void;
  showPinCodeInput?: boolean;
  flexRow?: boolean;
  hideCaption?: boolean;
  enterKeyHint?: InputEnterKeyHintType;
  onEnter?: () => void;
  alignIcon?: boolean;
};

type InputFieldType = TextInputFieldType | 'password';

export const getEnterKeyHint = (index: number, numberOfFields: number): InputEnterKeyHintType => {
  if (index === numberOfFields - 1) {
    return 'done';
  }

  return 'next';
};

const getInputType = ({ masked, numeric }: ProviderField): InputFieldType => {
  if (masked) {
    return 'password';
  }

  if (numeric) {
    return 'tel';
  }

  return 'text';
};

const getAutoCompleteType = (field: string): InputAutoCompleteType => {
  switch (field) {
    case 'username':
      return 'username';
    case 'password':
      return 'off';
    default:
      return 'on';
  }
};

export const Input = forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  const handleInputChange = (event: React.FormEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    const { name } = props.providerInputState.providerField;

    props.onChange({ providerInputName: name, value, onFormSubmit: false });
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key.toLowerCase() === 'enter') props.onEnter?.();
  };

  const { providerInputState, index, showPinCodeInput = false, alignIcon = false } = props;
  const { disabled, errorMessage, isValid, value, showCheckIcon } = providerInputState;
  const {
    name,
    description,
    hint,
    helpText,
    value: providerFieldValue,
    selectOptions,
    maxLength,
  } = providerInputState.providerField;
  const autoComplete = getAutoCompleteType(name);
  const inputType = getInputType(providerInputState.providerField);

  if (showPinCodeInput && maxLength) {
    return (
      <>
        {description && <H3>{description}</H3>}
        <PinCodeInput
          onChange={(e: PseudoInputEvent) =>
            props.onChange({ providerInputName: name, value: e.target.value, onFormSubmit: false })
          }
          name="PinCodeInput"
          type="number"
          autoFocus
          inputs={maxLength}
        />
      </>
    );
  }

  if (providerInputState.providerField.checkbox) {
    return (
      <CheckboxField
        field={providerInputState.providerField}
        value={providerInputState.value}
        onChange={(args) => {
          props.onChange({
            providerInputName: args.name,
            value: args.value,
            onFormSubmit: false,
          });
        }}
      />
    );
  }

  if (selectOptions) {
    const field = { ...providerInputState.providerField, selectOptions };

    return (
      <SelectProviderFieldOption
        field={field}
        value={providerInputState.value}
        errorMessage={errorMessage}
        onChange={(newValue: string) =>
          props.onChange({ providerInputName: name, value: newValue, onFormSubmit: false })
        }
      />
    );
  }

  if (providerFieldValue && isValid) {
    return (
      <StyledInputContainer>
        {description && (
          <StyledLabel htmlFor={providerInputState.providerField.name}>{description}</StyledLabel>
        )}

        {name === 'base64image' ? (
          <StyledImageField>
            <img
              src={`data:image/png;base64,${providerFieldValue}`}
              alt={description ?? ''}
              data-testid="image-input"
            />
          </StyledImageField>
        ) : (
          <StyledInputCode data-testid="input-code" id={name} value={providerFieldValue} />
        )}

        {helpText && !props.hideCaption && <Caption message={helpText} />}
      </StyledInputContainer>
    );
  }

  if (inputType === 'password') {
    return (
      <PasswordInputField
        ref={ref}
        onChange={handleInputChange}
        autoComplete={autoComplete}
        enterKeyHint={props.enterKeyHint}
        {...providerInputState}
      />
    );
  }

  return (
    <TextField
      forwardRef={ref}
      index={index}
      name={providerInputState.providerField.name}
      type={getInputType(providerInputState.providerField) as TextInputFieldType}
      description={description}
      placeholder={hint || ''}
      value={value || ''}
      disabled={disabled}
      autoComplete={autoComplete}
      helpCaption={helpText ?? undefined}
      errorCaption={errorMessage ?? undefined}
      hideCaption={props.hideCaption}
      endIcon={
        showCheckIcon ? { icon: <CheckIcon color="primary" data-testid="check-icon" /> } : undefined
      }
      flexRow={Boolean(props.flexRow)}
      enterKeyHint={props.enterKeyHint}
      onKeyDown={handleKeyDown}
      onChange={handleInputChange}
      alignIcon={alignIcon}
    />
  );
});
